import React, { useEffect, useState } from "react";
import { fmtMoney, useSSA } from "../SSAContext";
import { Card } from "../ui";

const Bars = ({ data, testid }) => {
  const entries = Object.entries(data || {});
  const max = Math.max(1, ...entries.map(([, v]) => v));
  if (!entries.length) return <p className="text-sm text-slate-400">No data</p>;
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {entries.sort((a, b) => b[1] - a[1]).map(([k, v]) => (
        <div key={k} className="flex items-center gap-2 text-xs">
          <span className="w-40 truncate text-slate-600" title={k}>{k}</span>
          <div className="flex-1 h-2 bg-slate-100 rounded-full"><div className="h-full rounded-full bg-[#0072CE]" style={{ width: `${(100 * v) / max}%` }} /></div>
          <span className="w-6 text-right font-semibold">{v}</span>
        </div>
      ))}
    </div>
  );
};

export default function InsightsPage() {
  const { call, refreshKey } = useSSA();
  const [incl, setIncl] = useState(true);
  const [r, setR] = useState(null);
  useEffect(() => { call("get", `/reports?include_demo=${incl}`, null, "procurement").then(setR).catch(() => {}); }, [incl, call, refreshKey]);
  if (!r) return null;
  const kpi = [["Requests", r.total_requests], ["RFQ response rate", `${r.rfq.response_rate}%`], ["Quotes released", r.quotes.released], ["Buyer acceptance", `${r.quotes.acceptance_rate}%`], ["Proposals / request", r.quotes.proposals_per_request]];
  return (
    <div className="space-y-5" data-testid="insights-page">
      <div className="flex flex-wrap items-end gap-3">
        <div className="mr-auto">
          <h1 className="font-display text-3xl font-semibold text-slate-900">Insights & reporting</h1>
          <p className="text-sm text-slate-500">Demand, RFQ, quote and commercial metrics. Live reports exclude demo records — untick “Include demo records” for live-only figures.</p>
        </div>
        <label className="flex items-center gap-2 text-sm bg-white border border-slate-200 rounded-lg px-3 py-2"><input type="checkbox" checked={incl} onChange={(e) => setIncl(e.target.checked)} data-testid="include-demo-toggle" /> Include demo records</label>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {kpi.map(([k, v]) => <div key={k} className="bg-white rounded-xl border border-slate-200 p-4"><div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{k}</div><div className="font-display text-3xl font-semibold text-[#0B2A4A]" data-testid={`insight-kpi-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</div></div>)}
      </div>
      <div className="grid lg:grid-cols-3 gap-5">
        <Card title="Requests by service"><Bars data={r.by_service} testid="insight-by-service" /></Card>
        <Card title="Requests by status"><Bars data={r.by_status} testid="insight-by-status" /></Card>
        <Card title="Readiness classification"><Bars data={r.by_readiness} testid="insight-by-readiness" /></Card>
        <Card title="Commercial visibility (accepted)" eyebrow="Separate measures">
          <table className="w-full text-sm" data-testid="insight-commercial"><tbody className="divide-y divide-slate-100">
            {[["Supplier cost", r.commercial.supplier_cost], ["Infosys fee", r.commercial.infosys_fee], ["Pass-throughs", r.commercial.pass_through], ["Taxes", r.commercial.tax], ["Total buyer spend", r.commercial.total]].map(([k, v]) => <tr key={k}><td className="py-1.5">{k}</td><td className="text-right font-mono">{fmtMoney(v)}</td></tr>)}
          </tbody></table>
        </Card>
        <Card title="RFQ metrics"><div className="text-sm space-y-1"><div>Invited: <b>{r.rfq.invited}</b></div><div>Responded: <b>{r.rfq.responded}</b></div><div className="text-xs text-slate-500">No-bid reasons: {r.rfq.declines.join("; ") || "—"}</div></div></Card>
        <Card title="Common missing fields"><Bars data={Object.fromEntries(r.common_missing_fields)} testid="insight-missing" /></Card>
        <Card title="Requests by country"><Bars data={r.by_country} testid="insight-by-country" /></Card>
      </div>
    </div>
  );
}
